/**
 * AnalyticsHelpers - Typed analytics tracking methods
 *
 * These functions provide typed tracking methods for common events
 * across web and mobile platforms. Event names are sourced from
 * EventCatalog — components should use these helpers instead of
 * calling track() with raw event name strings.
 *
 * Accepts a PostHog client (posthog.capture) or any object with
 * a .capture() or .track() method.
 */

import { EVENTS } from './EventCatalog';

/**
 * Create a typed analytics tracker with helper methods
 * @param {object} client - PostHog client or any object with capture()/track()
 * @returns {object} Object with typed tracking methods
 */
export const createAnalyticsHelpers = (client) => {
  // Support both PostHog (.capture) and legacy (.track) interfaces
  const capture = (eventName, properties) => {
    if (typeof client.capture === 'function') {
      client.capture(eventName, properties);
    } else if (typeof client.track === 'function') {
      client.track(eventName, properties);
    }
  };

  return {
    // ==========================================
    // AUTHENTICATION EVENTS
    // ==========================================

    trackSignup: async (method, properties = {}) => {
      capture(EVENTS.SIGNUP_COMPLETED, {
        signup_method: method,
        ...properties
      });
    },

    trackLogin: async (method, properties = {}) => {
      capture(EVENTS.LOGIN_COMPLETED, {
        login_method: method,
        ...properties
      });
    },

    trackLogout: async (properties = {}) => {
      capture(EVENTS.LOGOUT_COMPLETED, properties);
    },

    trackPasswordReset: async (properties = {}) => {
      capture(EVENTS.PASSWORD_RESET_COMPLETED, properties);
    },

    // ==========================================
    // ONBOARDING EVENTS
    // ==========================================

    trackOnboardingStep: async (step, properties = {}) => {
      capture(EVENTS.ONBOARDING_STEP_VIEWED, {
        step,
        ...properties
      });
    },

    trackOnboardingComplete: async (properties = {}) => {
      capture(EVENTS.ONBOARDING_COMPLETED, properties);
    },

    trackExamSelection: async (exam, properties = {}) => {
      capture(EVENTS.EXAM_SELECTED, {
        exam_name: exam,
        ...properties
      });
    },

    trackAssessmentStart: async (properties = {}) => {
      capture(EVENTS.ASSESSMENT_STARTED, properties);
    },

    trackAssessmentComplete: async (score, properties = {}) => {
      capture(EVENTS.ASSESSMENT_COMPLETED, {
        score,
        ...properties
      });
    },

    trackOnboardingAssessmentSkipped: async (properties = {}) => {
      capture(EVENTS.ONBOARDING_ASSESSMENT_SKIPPED, properties);
    },

    trackOnboardingAssessmentQuizCompleted: async (properties = {}) => {
      capture(EVENTS.ONBOARDING_ASSESSMENT_QUIZ_COMPLETED, properties);
    },

    trackOnboardingAssessmentFeedback: async (properties = {}) => {
      capture(EVENTS.ONBOARDING_ASSESSMENT_FEEDBACK, properties);
    },

    // ==========================================
    // STUDY EVENTS
    // ==========================================

    trackStudySessionStart: async (topic, properties = {}) => {
      capture(EVENTS.STUDY_SESSION_STARTED, {
        topic,
        ...properties
      });
    },

    trackStudySessionComplete: async (topic, duration, questionsAnswered, properties = {}) => {
      capture(EVENTS.STUDY_SESSION_COMPLETED, {
        topic,
        duration_seconds: duration,
        questions_answered: questionsAnswered,
        ...properties
      });
    },

    trackQuestionAnswer: async (questionId, isCorrect, topic, properties = {}) => {
      capture(EVENTS.QUESTION_ANSWERED, {
        question_id: questionId,
        is_correct: isCorrect,
        topic,
        ...properties
      });
    },

    trackQuestionBookmark: async (questionId, action, properties = {}) => {
      capture(EVENTS.QUESTION_BOOKMARKED, {
        question_id: questionId,
        action,
        ...properties
      });
    },

    trackStudyQuestionView: async (properties = {}) => {
      capture(EVENTS.STUDY_QUESTION_VIEWED, properties);
    },

    trackStudyQuestionsFiltered: async (filterType, properties = {}) => {
      capture(EVENTS.STUDY_QUESTIONS_FILTERED, {
        filter_type: filterType,
        ...properties
      });
    },

    // ==========================================
    // TEST EVENTS
    // ==========================================

    trackTestStart: async (testType, testId, properties = {}) => {
      capture(EVENTS.TEST_STARTED, {
        test_type: testType,
        test_id: testId,
        ...properties
      });
    },

    trackTestComplete: async (testType, testId, score, duration, properties = {}) => {
      capture(EVENTS.TEST_COMPLETED, {
        test_type: testType,
        test_id: testId,
        score,
        duration_seconds: duration,
        ...properties
      });
    },

    trackTestAbandon: async (testType, testId, reason, properties = {}) => {
      capture(EVENTS.TEST_ABANDONED, {
        test_type: testType,
        test_id: testId,
        reason,
        ...properties
      });
    },

    trackCustomTestCreate: async (domainCount, questionCount, properties = {}) => {
      capture(EVENTS.CUSTOM_TEST_CREATED, {
        domain_count: domainCount,
        question_count: questionCount,
        ...properties
      });
    },

    // ==========================================
    // FLASHCARD EVENTS
    // ==========================================

    trackFlashcardView: async (cardId, properties = {}) => {
      capture(EVENTS.FLASHCARD_VIEWED, {
        card_id: cardId,
        ...properties
      });
    },

    trackFlashcardAnswer: async (cardId, isCorrect, properties = {}) => {
      capture(EVENTS.FLASHCARD_ANSWERED, {
        card_id: cardId,
        is_correct: isCorrect,
        ...properties
      });
    },

    trackFlashcardBookmark: async (cardId, action, properties = {}) => {
      capture(EVENTS.FLASHCARD_BOOKMARKED, {
        card_id: cardId,
        action,
        ...properties
      });
    },

    trackFlashcardSessionStart: async (properties = {}) => {
      capture(EVENTS.FLASHCARD_SESSION_STARTED, properties);
    },

    trackFlashcardSessionEnd: async (duration, count, properties = {}) => {
      capture(EVENTS.FLASHCARD_SESSION_ENDED, {
        duration_seconds: duration,
        card_count: count,
        ...properties
      });
    },

    // ==========================================
    // SUBSCRIPTION EVENTS
    // ==========================================

    trackSubscriptionView: async (plan, properties = {}) => {
      capture(EVENTS.SUBSCRIPTION_VIEWED, {
        plan,
        ...properties
      });
    },

    trackSubscriptionStart: async (plan, properties = {}) => {
      capture(EVENTS.SUBSCRIPTION_STARTED, {
        plan,
        ...properties
      });
    },

    trackSubscriptionCancel: async (plan, properties = {}) => {
      capture(EVENTS.SUBSCRIPTION_CANCELLED, {
        plan,
        ...properties
      });
    },

    trackSubscriptionCancelClicked: async (plan, properties = {}) => {
      capture(EVENTS.SUBSCRIPTION_CANCEL_CLICKED, {
        plan,
        ...properties
      });
    },

    trackPurchaseIntent: async (planName, properties = {}) => {
      capture(EVENTS.PURCHASE_INTENT_STARTED, {
        plan_name: planName,
        ...properties
      });
    },

    trackPurchaseComplete: async (planName, amount, properties = {}) => {
      capture(EVENTS.PURCHASE_COMPLETED, {
        plan_name: planName,
        amount,
        ...properties
      });
    },

    // ==========================================
    // ARTICLE EVENTS
    // ==========================================

    trackArticleView: async (articleId, properties = {}) => {
      capture(EVENTS.ARTICLE_VIEWED, {
        article_id: articleId,
        ...properties
      });
    },

    trackArticleBookmark: async (articleId, action, properties = {}) => {
      capture(EVENTS.ARTICLE_BOOKMARKED, {
        article_id: articleId,
        action,
        ...properties
      });
    },

    trackArticleProgress: async (articleId, progress, properties = {}) => {
      capture(EVENTS.ARTICLE_PROGRESS_UPDATED, {
        article_id: articleId,
        progress,
        ...properties
      });
    },

    trackArticleReaction: async (articleId, action, properties = {}) => {
      capture(EVENTS.ARTICLE_REACTION, {
        article_id: articleId,
        action,
        ...properties
      });
    },

    // ==========================================
    // QOTD EVENTS
    // ==========================================

    trackQOTDView: async (properties = {}) => {
      capture(EVENTS.QOTD_VIEWED, properties);
    },

    trackQOTDAnswer: async (isCorrect, properties = {}) => {
      capture(EVENTS.QOTD_ANSWERED, {
        is_correct: isCorrect,
        ...properties
      });
    },

    // ==========================================
    // FEATURE USAGE EVENTS
    // ==========================================

    /** @deprecated Use track(EVENTS.XXX, props) or a specific typed helper instead. */
    trackFeatureUsage: async (feature, properties = {}) => {
      capture(EVENTS.FEATURE_USED, {
        feature,
        ...properties
      });
    },

    trackProfileUpdate: async (field, properties = {}) => {
      capture(EVENTS.PROFILE_UPDATED, {
        field,
        ...properties
      });
    },

    trackSearch: async (query, resultsCount, properties = {}) => {
      capture(EVENTS.SEARCH_COMPLETED, {
        query,
        results_count: resultsCount,
        ...properties
      });
    },

    trackShare: async (content, platform, properties = {}) => {
      capture(EVENTS.CONTENT_SHARED, {
        content,
        platform,
        ...properties
      });
    },

    // ==========================================
    // NAVIGATION & PAGE VIEW EVENTS
    // ==========================================

    trackPageView: async (pagePath, properties = {}) => {
      capture(EVENTS.PAGE_VIEWED, {
        page_path: pagePath,
        ...properties
      });
    },

    trackNavigation: async (from, to, properties = {}) => {
      capture(EVENTS.NAVIGATION, {
        from_page: from,
        to_page: to,
        ...properties
      });
    },

    trackScreenView: async (screenName, properties = {}) => {
      capture(screenName, {
        source: 'navigation',
        ...properties
      });
    },

    // ==========================================
    // SESSION & LIFECYCLE EVENTS
    // ==========================================

    trackSessionStart: async (properties = {}) => {
      capture(EVENTS.SESSION_STARTED, properties);
    },

    trackSessionPause: async (properties = {}) => {
      capture(EVENTS.SESSION_PAUSED, properties);
    },

    trackAppOpened: async (properties = {}) => {
      capture(EVENTS.APP_OPENED, properties);
    },

    // ==========================================
    // USER ACTION EVENTS
    // ==========================================

    trackUserAction: async (action, properties = {}) => {
      capture(EVENTS.USER_ACTION, {
        action,
        ...properties
      });
    },

    trackShareApp: async (properties = {}) => {
      capture(EVENTS.SHARE_APP, properties);
    },

    trackResetAppData: async (properties = {}) => {
      capture(EVENTS.RESET_APP_DATA, properties);
    },

    trackThemeChanged: async (theme, properties = {}) => {
      capture(EVENTS.APP_THEME_CHANGED, {
        theme,
        ...properties
      });
    },

    trackOfferClaimed: async (offerId, properties = {}) => {
      capture(EVENTS.OFFER_CLAIMED, {
        offer_id: offerId,
        ...properties
      });
    },

    // ==========================================
    // REVIEW & FEEDBACK EVENTS
    // ==========================================

    trackReviewApp: async (properties = {}) => {
      capture(EVENTS.REVIEW_APP, properties);
    },

    trackReviewFromHome: async (properties = {}) => {
      capture(EVENTS.REVIEW_FROM_HOME, properties);
    },

    trackReviewFeedback: async (feedbackType, properties = {}) => {
      // feedbackType: 'love_it', 'need_work', 'not_yet'
      const eventMap = {
        love_it: EVENTS.LOVE_IT_REVIEW,
        need_work: EVENTS.NEED_WORK_REVIEW,
        not_yet: EVENTS.NOT_REVIEW_YET,
      };
      capture(eventMap[feedbackType] || EVENTS.REVIEW_APP, properties);
    },

    // ==========================================
    // NOTIFICATION EVENTS
    // ==========================================

    trackNotificationPermission: async (granted, properties = {}) => {
      const event = granted
        ? EVENTS.NOTIFICATION_PERMISSION_GRANTED
        : EVENTS.NOTIFICATION_PERMISSION_DENIED;
      capture(event, properties);
    },

    // ==========================================
    // EXAM COUNTDOWN EVENTS
    // ==========================================

    trackExamCountdown: async (action, properties = {}) => {
      const eventMap = {
        set_clicked: EVENTS.EXAM_COUNTDOWN_SET_CLICKED,
        change_clicked: EVENTS.EXAM_COUNTDOWN_CHANGE_CLICKED,
        date_selected: EVENTS.EXAM_COUNTDOWN_DATE_SELECTED,
        updated: EVENTS.EXAM_COUNTDOWN_UPDATED,
      };
      capture(eventMap[action] || EVENTS.EXAM_COUNTDOWN_UPDATED, properties);
    },

    // ==========================================
    // READINESS & PROGRESS EVENTS
    // ==========================================

    trackReadinessScoreUpdated: async (score, previousScore, properties = {}) => {
      capture(EVENTS.READINESS_SCORE_UPDATED, {
        score,
        previous_score: previousScore,
        ...properties
      });
    },

    // ==========================================
    // ERROR & PERFORMANCE EVENTS
    // ==========================================

    trackAppError: async (errorType, errorMessage, properties = {}) => {
      capture(EVENTS.ERROR_OCCURRED, {
        error_type: errorType,
        error_message: errorMessage,
        ...properties
      });
    },

    trackPerformance: async (metric, value, properties = {}) => {
      capture(EVENTS.PERFORMANCE_MEASURED, {
        metric,
        value,
        ...properties
      });
    },

    trackPageLoadTime: async (loadTime, properties = {}) => {
      capture(EVENTS.PAGE_LOADED, {
        load_time_ms: loadTime,
        ...properties
      });
    },

    trackEngagement: async (action, duration, properties = {}) => {
      capture(EVENTS.ENGAGEMENT_RECORDED, {
        action,
        duration_seconds: duration,
        ...properties
      });
    }
  };
};

export default createAnalyticsHelpers;
